import "./style.css";
import WebTorrent from "webtorrent";
import debug from "debug";

// Create a debug logger for this app
const log = debug("wt-player");

// Handle unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  log("Unhandled promise rejection:", event.reason);
  // Don't prevent default - let console show these
});

// Default trackers (always included)
const DEFAULT_TRACKERS = [
  "ws://185.18.221.66:3000/",
  "wss://tracker.webtorrent.dev",
  "wss://tracker.openwebtorrent.com",
  "wss://tracker.files.fm:7073/announce",
];

// Dynamic tracker list (will be populated from GitHub)
let trackerList = [...DEFAULT_TRACKERS];

// Fetch fresh tracker list from GitHub
async function fetchTrackerList() {
  log("Fetching tracker list from GitHub...");
  const response = await fetch(
    "https://raw.githubusercontent.com/ngosang/trackerslist/master/trackers_all_ws.txt",
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const text = await response.text();
  const trackers = text
    .split("\n")
    .map((line) => line.trim())
    .filter(
      (line) =>
        line.length > 0 &&
        (line.startsWith("wss://") || line.startsWith("ws://")),
    );

  if (trackers.length === 0) {
    throw new Error("No valid trackers found in response");
  }

  // Always include default trackers first, then add others (remove duplicates)
  const uniqueTrackers = new Set([...DEFAULT_TRACKERS, ...trackers]);
  trackerList = Array.from(uniqueTrackers);

  log(
    `Loaded ${trackerList.length} WebSocket trackers (including ${DEFAULT_TRACKERS.length} defaults)`,
  );
  console.log(`Successfully loaded ${trackerList.length} WebSocket trackers`);
}

const video = document.getElementById("video");
const magnetInput = document.getElementById("magnetInput");
const loadBtn = document.getElementById("loadBtn");
const stopBtn = document.getElementById("stopBtn");
const status = document.getElementById("status");
const progress = document.getElementById("progress");
const progressBar = document.getElementById("progressBar");
const fileList = document.getElementById("fileList");
const title = document.getElementById("title");
const controls = document.getElementById("controls");

let client = null;
let currentTorrent = null;
let targetFilePath = null; // Store target file path from hash fragment

// Initialize WebTorrent with error handling and configuration
async function initializeClient() {
  try {
    setStatus("Loading tracker list...", "info");

    // Fetch fresh tracker list first
    await fetchTrackerList();

    // Register service worker for streaming support
    if ("serviceWorker" in navigator) {
      setStatus("Registering service worker...", "info");

      const registration = await navigator.serviceWorker.register(
        "/sw.min.js",
        { scope: "/" },
      );

      log("Service worker registered:", registration);

      // Wait for service worker to be activated
      const worker = registration.active || registration.waiting || registration.installing;

      if (worker && worker.state === "activated") {
        await createWebTorrentClient(registration);
      } else if (worker) {
        await new Promise((resolve) => {
          worker.addEventListener("statechange", () => {
            if (worker.state === "activated") {
              resolve();
            }
          });
        });
        await createWebTorrentClient(registration);
      }
    } else {
      // Fallback without service worker
      log("Service worker not supported, using fallback mode");
      await createWebTorrentClient(null);
    }
  } catch (err) {
    console.error("Failed to initialize WebTorrent:", err);
    setStatus(
      "Error: Failed to initialize WebTorrent. Try a different browser.",
      "error",
    );
    loadBtn.disabled = true;
  }
}

async function createWebTorrentClient(registration) {
  const config = {
    dht: false, // DHT doesn't work in browser, disable it
    tracker: {
      announce: trackerList,
    }
  };

  client = new WebTorrent(config);

  // Create server for streaming if service worker is available
  if (registration) {
    client.createServer({ controller: registration });
    log("WebTorrent server created with service worker");
  }

  console.log("WebTorrent initialized successfully");
  log("WebTorrent client created with", trackerList.length, "trackers");

  setStatus(`Ready (${trackerList.length} trackers loaded)`, "success");

  // Parse hash fragment now that client is ready
  parseHashFragment();
}

// Initialize on load
initializeClient();

function setStatus(message, type = "info") {
  status.textContent = message;
  status.className = `status ${type}`;
}

function updateProgress(percent) {
  progress.style.width = `${percent}%`;

  // Show progress bar only when there is progress > 0
  if (percent > 0) {
    progressBar.style.display = "block";
  } else {
    progressBar.style.display = "none";
  }
}

function displayFileList(torrent) {
  // Only include formats natively supported by browsers (no transcoding needed)
  const videoFiles = torrent.files.filter((file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    // Only MP4, WebM, and OGG are widely supported without transcoding
    return ["mp4", "webm", "ogg"].includes(ext);
  });

  if (videoFiles.length === 0) {
    setStatus("No playable video files found in torrent", "error");
    return;
  }

  // Check if a specific file path was requested
  if (targetFilePath) {
    log("Looking for specific file:", targetFilePath);

    // Try to find file by exact path match
    let targetFile = videoFiles.find((file) => file.path === targetFilePath);

    // If not found, try matching just the filename
    if (!targetFile) {
      const targetFilename = targetFilePath.split("/").pop();
      targetFile = videoFiles.find((file) => file.name === targetFilename);
      log("Trying filename match:", targetFilename);
    }

    // If not found, try partial path matching
    if (!targetFile) {
      targetFile = videoFiles.find((file) =>
        file.path.includes(targetFilePath),
      );
      log("Trying partial path match");
    }

    if (targetFile) {
      log("Found target file:", targetFile.path);
      fileList.style.display = "none";
      playFile(targetFile);
      targetFilePath = null; // Clear after use
      return;
    } else {
      log("Target file not found, showing all files");
      setStatus(
        `File "${targetFilePath}" not found in torrent, showing all files`,
        "info",
      );
      targetFilePath = null;
    }
  }

  // Show file list if multiple files or no specific file requested
  if (videoFiles.length > 1) {
    fileList.style.display = "block";
    fileList.innerHTML = "<strong>Select a file to play:</strong>";

    videoFiles.forEach((file) => {
      const item = document.createElement("div");
      item.className = "file-item";
      item.textContent = `${file.name} (${(file.length / 1024 / 1024).toFixed(2)} MB)`;
      item.onclick = () => {
        document
          .querySelectorAll(".file-item")
          .forEach((el) => el.classList.remove("selected"));
        item.classList.add("selected");
        playFile(file);
      };
      fileList.appendChild(item);
    });
  } else if (videoFiles.length === 1) {
    fileList.style.display = "none";
    playFile(videoFiles[0]);
  }
}

function playFile(file) {
  log("Playing file:", file.name);

  try {
    // Use streamTo for browser-based streaming with MediaSource
    file.streamTo(video);

    // Listen for video element events
    video.addEventListener('loadedmetadata', () => {
      log("Video metadata loaded for:", file.name);
      setStatus(`Playing: ${file.name}`, "success");
    }, { once: true });

    video.addEventListener('error', (err) => {
      console.error("Error playing video:", err, video.error);
      log("Error playing video:", err);
      setStatus(`Error playing file: ${video.error?.message || 'Video error'}`, "error");
    }, { once: true });

    video.autoplay = true;
  } catch (err) {
    console.error("Error streaming file:", err);
    log("Error streaming file:", err);
    setStatus(`Error playing file: ${err.message}`, "error");
  }
}

function loadTorrent(magnetURI) {
  if (!client) {
    setStatus("WebTorrent client not initialized", "error");
    return;
  }

  if (currentTorrent) {
    log("Destroying previous torrent");
    currentTorrent.destroy();
  }

  setStatus("Loading torrent...", "info");
  updateProgress(0);
  loadBtn.disabled = true;
  stopBtn.disabled = false;

  log("Adding torrent:", magnetURI);

  try {
    // Add torrent with dynamic tracker list
    currentTorrent = client.add(
      magnetURI,
      {
        announce: trackerList,
      },
      (torrent) => {
        log("Torrent metadata loaded:", torrent.name);
        log("Number of files:", torrent.files.length);
        log("Announce list:", torrent.announce);

        setStatus(`Torrent loaded: ${torrent.name}`, "success");
        displayFileList(torrent);

        torrent.on("download", () => {
          const percent = Math.round(torrent.progress * 100);
          updateProgress(percent);
          const speedMBps = (torrent.downloadSpeed / 1024 / 1024).toFixed(2);
          const downloadedMB = (torrent.downloaded / 1024 / 1024).toFixed(2);
          const totalMB = (torrent.length / 1024 / 1024).toFixed(2);
          setStatus(
            `Downloading: ${percent}% (${downloadedMB}/${totalMB} MB, ${speedMBps} MB/s, ${torrent.numPeers} peers)`,
            "info",
          );
        });

        torrent.on("upload", () => {
          const uploadSpeedMBps = (torrent.uploadSpeed / 1024 / 1024).toFixed(
            2,
          );
          log(`Upload speed: ${uploadSpeedMBps} MB/s`);
        });

        torrent.on("wire", (wire) => {
          log("Connected to peer:", wire.remoteAddress);
        });

        torrent.on("done", () => {
          log("Download complete!");
          setStatus("Download complete!", "success");
          updateProgress(100);
        });
      },
    );

    currentTorrent.on("error", (err) => {
      console.error("Torrent error:", err);
      log("Torrent error:", err);
      setStatus(`Error: ${err.message}`, "error");
      loadBtn.disabled = false;
      stopBtn.disabled = true;
    });

    currentTorrent.on("warning", (err) => {
      console.warn("Torrent warning:", err);
      log("Torrent warning:", err);
    });
  } catch (err) {
    console.error("Failed to add torrent:", err);
    log("Failed to add torrent:", err);
    setStatus(`Error: ${err.message}`, "error");
    loadBtn.disabled = false;
    stopBtn.disabled = true;
  }
}

function parseHashFragment() {
  const hash = window.location.hash.substring(1);
  if (!hash) return;

  // Enable embed mode: hide UI, show only player and status
  log("Hash fragment detected - enabling embed mode");
  document.body.classList.add("embed-mode");
  title.style.display = "none";

  // Hide input and buttons, but keep status and progress visible
  document.querySelector(".input-group").style.display = "none";
  document.querySelector(".button-group").style.display = "none";

  log("Parsing hash fragment:", hash.substring(0, 50) + "...");

  try {
    // First, try to decode as base64 JSON
    try {
      const decoded = atob(hash);
      const data = JSON.parse(decoded);

      log("Parsed base64 JSON:", data);

      if (data.magnet) {
        magnetInput.value = data.magnet;

        // Store path for automatic file selection
        if (data.path) {
          targetFilePath = data.path;
          log("Target file path set:", targetFilePath);
        }

        loadTorrent(data.magnet);
      } else {
        throw new Error("No magnet property in JSON");
      }
    } catch (jsonError) {
      // If base64/JSON parsing fails, try legacy raw magnet link
      const decoded = decodeURIComponent(hash);

      if (decoded.startsWith("magnet:")) {
        log("Using legacy raw magnet link format");
        magnetInput.value = decoded;
        loadTorrent(decoded);
      } else {
        // Not base64 JSON and not a magnet link
        log("Hash fragment not recognized:", decoded);
        magnetInput.value = decoded;
      }
    }
  } catch (err) {
    console.error("Error parsing hash fragment:", err);
    log("Error parsing hash fragment:", err);
    setStatus("Error: Invalid hash fragment format", "error");
  }
}

loadBtn.addEventListener("click", () => {
  const magnetURI = magnetInput.value.trim();
  if (magnetURI) {
    loadTorrent(magnetURI);
  } else {
    setStatus("Please enter a magnet link or torrent URL", "error");
  }
});

stopBtn.addEventListener("click", () => {
  if (currentTorrent) {
    currentTorrent.destroy();
    currentTorrent = null;
    video.src = "";
    fileList.style.display = "none";
    setStatus("Stopped", "info");
    updateProgress(0);
    loadBtn.disabled = false;
    stopBtn.disabled = true;
  }
});

// Listen for hash changes
window.addEventListener("hashchange", parseHashFragment);

// Note: Hash parsing is done in initializeClient() after client is ready

// Allow Enter key to load torrent
magnetInput.addEventListener("keypress", (e) => {
  if (e.key === "Enter") {
    loadBtn.click();
  }
});
